import EmojiPicker, {
  Categories,
  EmojiClickData,
  Theme,
} from "emoji-picker-react";
import { AnimatePresence, motion } from "framer-motion";
import React, { FC, useState } from "react";
import { defaultEmojis } from "./utils/DefaultEmojis";
import { PlusIcon } from "lucide-react";
import { Button } from "@capacit-com/lara-law-shared-ui";
import useSettingsStore from "@/stores/settings.store";
import { WorkspaceComment } from "@/models/workspace/WorkspaceComment";
import useUserStore from "@/stores/user.store";
import { cn } from "@/lib/utils";
import { logger } from "@/lib/logger";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@capacit-com/lara-law-shared-ui";
import { Spinner } from "@capacit-com/lara-law-shared-ui";
import useWorkspaceComments from "@/hooks/useWorkspaceComments";
import useCommentsStore from "@/stores/comments.store";

type ReactionFooterProps = {
  comment: WorkspaceComment;
};

const ReactionFooter: FC<ReactionFooterProps> = ({ comment }) => {
  // Hooks
  const { isDarkMode } = useSettingsStore();
  const { filter } = useCommentsStore();
  const { updateCommentReaction } = useWorkspaceComments();
  const { me } = useUserStore();

  // State
  const [isEmojiPickerOpen, setIsEmojiPickerOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [reactionLoading, setReactionLoading] = useState<
    Record<string, boolean>
  >({});

  // Handle functions
  const handleEmojiClick = async (emoji: EmojiClickData) => {
    setReactionLoading((prev) => ({ ...prev, [emoji.unified]: true }));
    setIsEmojiPickerOpen(false);
    try {
      await updateCommentReaction.mutateAsync({
        id: comment.id,
        reaction: emoji.unified,
        fileId: filter !== "workspace" ? filter : undefined,
      });
    } catch (error) {
      logger.error("Failed to update workspace comment reaction", {
        operation: "updateWorkspaceCommentReaction",
        error,
        workspaceId: comment.workspaceId,
        commentId: comment.id,
      });
    } finally {
      setReactionLoading((prev) => ({ ...prev, [emoji.unified]: false }));
    }
  };

  return (
    <div className="relative inline-block">
      <div
        className="relative flex items-center h-5 gap-1"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Container for both elements */}
        <div className="flex items-center h-6 overflow-hidden relative">
          <AnimatePresence mode="wait">
            {/* Emoji reactions */}
            {isHovered && (
              <motion.div
                key="emojis"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                transition={{ duration: 0.2 }}
                className="flex items-center gap-0.5"
              >
                {defaultEmojis.map((emoji, index) => (
                  <React.Fragment key={index}>
                    {/* Loading state for each emoji */}
                    {reactionLoading[emoji.unified] ? (
                      <div className="flex items-center bg-accent dark:bg-accent-foreground/10 rounded-sm p-1 px-1.5 transition-all duration-200">
                        <Spinner className="size-4" />
                      </div>
                    ) : (
                      <div
                        className={cn(
                          "flex items-center cursor-pointer hover:bg-accent dark:hover:bg-accent-foreground/10 rounded-sm px-1.5 transition-all duration-200",
                          me?.id &&
                            comment.reactions?.[emoji.unified]?.includes(
                              me.id
                            ) &&
                            !Object.values(reactionLoading).some(
                              (isLoading) => isLoading
                            ) &&
                            "bg-accent dark:bg-accent-foreground/10 text-accent-foreground"
                        )}
                        onClick={() => handleEmojiClick(emoji)}
                      >
                        {emoji.emoji}
                      </div>
                    )}
                  </React.Fragment>
                ))}
              </motion.div>
            )}

            {/* "Reager" button */}
            {!isHovered && (
              <div>
                <Button variant="ghost" size="sm" className="h-6">
                  Reager
                </Button>
              </div>
            )}
          </AnimatePresence>

          {/* Move Popover outside AnimatePresence */}
          <Popover>
            <PopoverTrigger asChild>
              {isHovered && (
                <motion.div
                  key="show-reactions"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  transition={{ duration: 0.2 }}
                  className="flex items-center gap-1"
                >
                  <div
                    className="cursor-pointer ml-0.5 rounded-sm p-1 px-1.5 transition-all duration-200 hover:bg-accent dark:hover:bg-accent-foreground/10"
                    onClick={() => setIsEmojiPickerOpen(true)}
                  >
                    <PlusIcon className="size-4" />
                  </div>
                </motion.div>
              )}
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0 mr-8 rounded-[0.56rem]">
              <EmojiPicker
                theme={isDarkMode ? Theme.DARK : Theme.LIGHT}
                onEmojiClick={handleEmojiClick}
                previewConfig={{ showPreview: false }}
                skinTonesDisabled={true}
                open={isEmojiPickerOpen}
                categories={[
                  { category: Categories.SUGGESTED, name: "Seneste" },
                  {
                    category: Categories.SMILEYS_PEOPLE,
                    name: "Emojies & Personer",
                  },
                  {
                    category: Categories.ANIMALS_NATURE,
                    name: "Dyr & Natur",
                  },
                  {
                    category: Categories.FOOD_DRINK,
                    name: "Mad & Drikke",
                  },
                  {
                    category: Categories.TRAVEL_PLACES,
                    name: "Rejser & Steder",
                  },
                  {
                    category: Categories.ACTIVITIES,
                    name: "Aktiviteter",
                  },
                  { category: Categories.OBJECTS, name: "Objekter" },
                  { category: Categories.SYMBOLS, name: "Symboler" },
                  { category: Categories.FLAGS, name: "Flag" },
                ]}
              />
            </PopoverContent>
          </Popover>
        </div>
      </div>
    </div>
  );
};

export default ReactionFooter;
